import { Map as ImmutableMap } from 'immutable';
import Raven from 'raven-js';
import { useState, useMemo } from 'react';
import I18n from 'I18n';
import { ReportWithOptions } from 'reporting-snowflake/relational/resolve/resolve-records';
import { getTableList } from 'reporting-snowflake/relational/utils/table-utils';
import { buildTopPropertiesByObjectType, getTopPropertyData } from 'reporting-snowflake/relational/utils/top-property-utils';
import { sortOptionsByText } from 'reporting-snowflake/shared/lib/utils';
export const useTopProperties = ({
  relationalReport,
  allJoinMetadata,
  fullTableAndPropertyMeta,
  allAvailableColumns
}) => {
  const [topPropertiesMap, setTopPropertiesMap] = useState(ImmutableMap());
  const [isLoading, setIsLoading] = useState(true);
  const table = relationalReport.table;
  const reportWithOptions = ReportWithOptions({
    reportDefinition: relationalReport,
    reportOptions: {}
  });
  useMemo(() => {
    const tableList = getTableList(table);
    buildTopPropertiesByObjectType(tableList).then(response => {
      const newTopProperties = ImmutableMap(response.map(tableItem => [tableItem.key, tableItem.value]));
      setTopPropertiesMap(newTopProperties);
      setIsLoading(false);
    }).catch(err => {
      Raven.captureException(err);
      setTopPropertiesMap(ImmutableMap());
      setIsLoading(false);
    });
  }, [table]);
  if (!fullTableAndPropertyMeta.data) {
    return {
      isTopPropertiesLoading: true,
      allTopProperties: []
    };
  }
  if (allAvailableColumns && allAvailableColumns.size === 0) {
    return {
      isTopPropertiesLoading: false,
      allTopProperties: []
    };
  }
  if (isLoading) {
    return {
      isTopPropertiesLoading: true,
      allTopProperties: []
    };
  }
  const {
    tables,
    properties
  } = fullTableAndPropertyMeta.data;
  const topPropertyData = getTopPropertyData({
    tableDescription: table,
    properties,
    tables,
    persistedReport: reportWithOptions,
    topProperties: topPropertiesMap,
    allJoinMetadata
  });
  if (!topPropertyData || topPropertyData.size === 0 || topPropertyData.size === 1 && topPropertyData.first().length === 0) {
    return {
      isTopPropertiesLoading: false,
      allTopProperties: []
    };
  }
  const allTopProperties = topPropertyData.reduce((accumulator, propertyGroup) => {
    if (!propertyGroup || propertyGroup.length === 0) {
      return accumulator;
    }
    accumulator.push({
      text: I18n.text('report-customization-components.customization-components.relational-report.top-properties', {
        propertyName: propertyGroup[0].sourceLabel
      }),
      value: I18n.text('report-customization-components.customization-components.relational-report.top-properties', {
        propertyName: propertyGroup[0].sourceLabel
      }),
      options: propertyGroup.map(property => {
        const field = property.value;
        return {
          text: property.text,
          value: `${field.table}.${field.name}`,
          disabled: Boolean(property.errorMessage || property.warningMessage),
          help: property.errorMessage || property.warningMessage ? I18n.text('report-customization-components.customization-components.relational-report.default-measures-help') : undefined
        };
      }).sort(sortOptionsByText)
    });
    return accumulator;
  }, []);
  return {
    isTopPropertiesLoading: isLoading,
    allTopProperties
  };
};
export default useTopProperties;